import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    RefreshCw, Calendar, UserPlus,
    CalendarCheck, ClipboardList, BarChart3,
    ChevronRight, ChevronLeft, ChevronDown, Users, UserCheck,
    Bell, RotateCcw, Truck, PackageX,
    CheckCircle, XCircle, Clock, X, PhoneOutgoing, PhoneCall,
} from 'lucide-react';
import WhatsAppIcon from './icons/WhatsAppIcon';
import { GlobalSearch } from './GlobalSearch';
import { cn } from '@/utils/cn';
import { useUser } from '@/contexts/UserContext';
import { claimsService, notificationsService, InAppNotification } from '@/services/claimsService';
import { whatsappInboxService } from '@/services/integrations/whatsappInboxService';
import { useWebSocket } from '@/hooks/useWebSocket';
import UserProfileDropdown from './UserProfileDropdown';
import { showToast } from '@/components/ui/Toast';
import { format } from 'date-fns';

const STORAGE_KEY = 'zh:sidebar:expanded';

interface NavChild {
    label: string;
    href: string;
    icon?: React.ReactElement;
}

interface NavItem {
    icon: React.ReactElement;
    label: string;
    href: string;
    badge?: number;
    children?: NavChild[];
    isGroup?: boolean;
}

interface AppSidebarProps {
    isExpanded: boolean;
    setIsExpanded: (v: boolean) => void;
}

const notifIcon = (type: string) => {
    if (type === 'claim_approved') return <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />;
    if (type === 'claim_declined') return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
    if (type === 'event_reminder') return <Bell className="h-4 w-4 text-blue-500 shrink-0" />;
    return <Clock className="h-4 w-4 text-yellow-500 shrink-0" />;
};

const safeDate = (d: string) => {
    try { return format(new Date(d), 'dd MMM, hh:mm a'); } catch { return d; }
};

const AppSidebar: React.FC<AppSidebarProps> = ({ isExpanded, setIsExpanded }) => {
    const location = useLocation();
    const { userData } = useUser();
    const isSuperAdmin = userData?.data?.role?.role_id === 9401;

    const navigate = useNavigate();
    const [flyoutItem, setFlyoutItem] = useState<string | null>(null);
    // Flyout anchor: items in the lower half of the screen open upward (bottom-aligned to
    // the icon) so long submenus like Reports stay on screen.
    const [flyoutPos, setFlyoutPos] = useState<{ top?: number; bottom?: number; maxHeight?: number }>({ top: 0 });
    const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
    const [pendingClaimsCount, setPendingClaimsCount] = useState(0);
    const [waUnreadCount, setWaUnreadCount] = useState(0);
    const flyoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Notification state
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<InAppNotification[]>([]);
    const [showNotifPanel, setShowNotifPanel] = useState(false);
    const notifRef = useRef<HTMLDivElement>(null);

    const toggleGroup = (label: string) => setOpenGroups(prev => {
        if (prev.has(label)) return new Set();
        return new Set([label]);
    });

    useEffect(() => { localStorage.setItem(STORAGE_KEY, String(isExpanded)); }, [isExpanded]);

    useEffect(() => {
        claimsService.getPendingCount()
            .then(res => { if (res.success && res.data) setPendingClaimsCount((res.data as any).count || 0); })
            .catch(() => {});
        notificationsService.getUnreadCount()
            .then(res => { if (res.success && res.data) setUnreadCount((res.data as any).count || 0); })
            .catch(() => {});
    }, []);

    // WhatsApp Inbox unread badge — polled rather than a dedicated socket
    // (the Inbox page itself gets true live push via its own scoped
    // WhatsAppSocketProvider; the sidebar just needs a roughly-fresh count).
    useEffect(() => {
        const loadWaUnread = () => {
            whatsappInboxService.listConversations({ unread_only: true, page: 1, page_size: 1 })
                .then(res => setWaUnreadCount(res.total || 0))
                .catch(() => {});
        };
        loadWaUnread();
        const id = setInterval(loadWaUnread, 30_000);
        return () => clearInterval(id);
    }, []);

    useWebSocket((msg) => {
        if (msg.type === 'init') {
            if (msg.notif_count !== undefined) setUnreadCount(msg.notif_count);
            if (msg.claims_count !== undefined) setPendingClaimsCount(msg.claims_count);
        } else if (msg.type === 'notif_inc') setUnreadCount(c => c + 1);
        else if (msg.type === 'claims_inc') setPendingClaimsCount(c => c + 1);
        else if (msg.type === 'claims_dec') setPendingClaimsCount(c => Math.max(0, c - 1));
    });

    // Close notif panel on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifPanel(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const openNotifPanel = async () => {
        if (!showNotifPanel) {
            try {
                const res = await notificationsService.list();
                if (res.success && res.data) setNotifications(res.data as any);
            } catch { /* silent */ }
        }
        setShowNotifPanel(v => !v);
    };

    const markRead = async (notif: InAppNotification) => {
        if (!notif.is_read) {
            await notificationsService.markRead(notif.uuid);
            setNotifications(prev => prev.map(n => n.uuid === notif.uuid ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
        if (notif.lead_uuid) {
            window.open(`/lead/${notif.lead_uuid}`, '_blank');
            setShowNotifPanel(false);
        }
    };

    const markAllRead = async () => {
        try {
            await notificationsService.markAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
            showToast('All notifications marked as read', 'success');
        } catch { /* silent */ }
    };

    const isActive = useCallback((href: string) => {
        const clean = location.pathname.replace(/^\//, '');
        if (href === 'dashboard' && (clean === 'dashboard' || clean === '')) return true;
        if (href === 'leads' && (clean === 'leads' || clean === 'pipeline')) return true;
        if (href === 'claims' && clean.startsWith('claims')) return true;
        if (href === 'reports' && clean.startsWith('reports')) return true;
        if (href === 'shopify-admin' && clean.startsWith('shopify-admin')) return true;
        if (href === 'settings' && clean.startsWith('settings')) return true;
        if (href === 'rto-leads' && clean === 'rto-leads') return true;
        if (href === 'edd-leads' && clean === 'edd-leads') return true;
        if (href === 'failed-delivery-leads' && clean === 'failed-delivery-leads') return true;
        return clean === href;
    }, [location.pathname]);

    const mainItems: NavItem[] = [
        {
            icon: <Users size={18} />,
            label: 'Sales Team',
            href: 'sales-team',
            isGroup: true,
            children: [
                { label: 'Abandoned Cart Leads', href: '/leads', icon: <Calendar size={14} /> },
                { label: 'Agent Leads', href: '/agent-orders', icon: <UserPlus size={14} /> },
                { label: 'Phone Leads', href: '/phone-leads', icon: <PhoneCall size={14} /> },
                // COD orders to confirm before the courier picks them up (~6 PM).
                // Hidden until management approves; the backend is off too
                // (COD_CONFIRM_ENABLED). Uncomment together with enabling that flag.
                // { label: 'Confirm Orders', href: '/confirm-orders', icon: <ShieldCheck size={14} /> },
            ],
        },
        {
            icon: <UserCheck size={18} />,
            label: 'Intransit Team',
            href: 'onboarding-team',
            isGroup: true,
            children: [
                // Order follows the shipment lifecycle: in transit -> attempt failed -> returned.
                { label: 'EDD', href: '/edd-leads', icon: <Truck size={14} /> },
                { label: 'NDR', href: '/failed-delivery-leads', icon: <PackageX size={14} /> },
                { label: 'RTO', href: '/rto-leads', icon: <RotateCcw size={14} /> },
            ],
        },
        {
            icon: <RefreshCw size={18} />,
            label: 'Retention Team',
            href: 'retention-team',
            isGroup: true,
            children: [
                { label: 'Re Orders', href: '/reorder-leads', icon: <RefreshCw size={14} /> },
                { label: 'Journey Calls', href: '/journey-calls', icon: <PhoneCall size={14} /> },
            ],
        },
        { icon: <PhoneOutgoing size={18} />, label: 'Dialer Campaigns', href: 'campaign-upload' },
        { icon: <CalendarCheck size={18} />, label: 'Calendar', href: 'activities' },
        { icon: <WhatsAppIcon size={18} />, label: 'WhatsApp', href: 'whatsapp-inbox', badge: waUnreadCount },
    ];

    const mgmtItems: NavItem[] = [
        {
            icon: <ClipboardList size={18} />,
            label: 'Claims',
            href: 'claims',
            badge: isSuperAdmin ? pendingClaimsCount : 0,
            children: [
                { label: 'Pending Claims', href: '/claims/pending' },
                { label: 'All Claims', href: '/claims/all' },
                { label: 'My Claims', href: '/claims/mine' },
                { label: 'Claims Reports', href: '/claims/reports' },
            ],
        },
    ];

    const adminItems: NavItem[] = [
        {
            icon: <BarChart3 size={18} />,
            label: 'Reports',
            href: 'reports',
            children: [
                { label: 'ABC Report', href: '/reports/abc' },
                { label: 'Call Center Management Dashboard', href: '/reports/management-dashboard' },
                // { label: 'ABC Daily', href: '/reports/abc-daily' },      // hidden from the menu
                // { label: 'ABC Cohorts', href: '/reports/abc-cohorts' },   // hidden from the menu
                // { label: 'Engati AI Calls', href: '/reports/engati' },  // hidden: the Engati tie was removed
                // { label: 'Agent Leaderboard', href: '/reports/incentive-leaderboard' },    // hidden from the menu
                // { label: 'Agent Dashboard (Beta)', href: '/reports/agent-dashboard' },     // hidden from the menu
                { label: 'Performance Report', href: '/reports/performance-report' },
                { label: 'Customer Profiles', href: '/reports/customer-profiles' },
                { label: 'Not Dispatched', href: '/reports/not-dispatched' },
                { label: 'Daily Health', href: '/reports/daily-health' },
                ...(isSuperAdmin ? [{ label: 'Call Recordings', href: '/reports/call-recordings' }] : []),
            ],
        },
    ];

    const clearFlyoutTimer = () => { if (flyoutTimer.current) clearTimeout(flyoutTimer.current); };

    const handleItemEnter = (label: string, e: React.MouseEvent<HTMLDivElement>) => {
        clearFlyoutTimer();
        if (!isExpanded) {
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            const openUp = rect.top > window.innerHeight / 2;
            if (openUp) {
                const bottom = Math.max(window.innerHeight - rect.bottom, 8);
                setFlyoutPos({ bottom, maxHeight: window.innerHeight - bottom - 8 });
            } else {
                setFlyoutPos({ top: rect.top, maxHeight: window.innerHeight - rect.top - 8 });
            }
            setFlyoutItem(label);
        }
    };

    const handleItemLeave = () => {
        flyoutTimer.current = setTimeout(() => setFlyoutItem(null), 120);
    };

    const renderItem = (item: NavItem) => {
        const active = isActive(item.href);
        const hasChildren = !!(item.children?.length);
        const isFlyoutOpen = flyoutItem === item.label;
        const isOpen = openGroups.has(item.label);

        const groupActive = item.isGroup
            ? !!(item.children?.some(child =>
                child.href === '/leads'
                    ? location.pathname === '/leads' || location.pathname === '/pipeline'
                    : location.pathname === child.href
            ))
            : false;

        const itemActive = item.isGroup ? groupActive : active;

        return (
            <div
                key={item.label}
                className="relative"
                onMouseEnter={(e) => handleItemEnter(item.label, e)}
                onMouseLeave={handleItemLeave}
            >
                {/* Active left-border indicator */}
                {itemActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white rounded-r-full pointer-events-none z-10" />
                )}

                {/* Group header — section label style */}
                {item.isGroup ? (
                    <button
                        onClick={() => {
                            if (!isExpanded && item.children?.length === 1) {
                                navigate(item.children[0].href);
                            } else if (isExpanded) {
                                toggleGroup(item.label);
                            }
                        }}
                        className={cn(
                            'w-full flex items-center gap-3 transition-colors duration-150',
                            isExpanded ? 'pl-4 pr-3 py-2' : 'justify-center py-2.5',
                            groupActive ? 'text-white' : 'text-white/55 hover:text-white/90'
                        )}
                    >
                        <div className="shrink-0 flex items-center justify-center w-5">{item.icon}</div>
                        {isExpanded && (
                            <>
                                <span className="text-[13px] flex-1 text-left">{item.label}</span>
                                {hasChildren && <ChevronDown size={12} className={cn('shrink-0 text-white/30 transition-transform duration-200', isOpen && 'rotate-180')} />}
                            </>
                        )}
                    </button>
                ) : hasChildren && isExpanded ? (
                    <button
                        onClick={() => toggleGroup(item.label)}
                        className={cn(
                            'w-full flex items-center gap-3 transition-colors duration-150 pl-4 pr-3 py-2',
                            itemActive ? 'text-white' : 'text-white/55 hover:text-white/90'
                        )}
                    >
                        <div className="relative shrink-0 flex items-center justify-center w-5">
                            {item.icon}
                            {item.badge !== undefined && item.badge > 0 && (
                                <span className="absolute -top-2 -right-2.5 flex items-center justify-center min-w-[15px] h-[15px] px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full">
                                    {item.badge > 99 ? '99+' : item.badge}
                                </span>
                            )}
                        </div>
                        <span className="text-[13px] flex-1 whitespace-nowrap overflow-hidden text-ellipsis text-left">{item.label}</span>
                        <ChevronDown size={12} className={cn('shrink-0 text-white/30 transition-transform duration-200', isOpen && 'rotate-180')} />
                    </button>
                ) : (
                    <Link
                        to={`/${item.href}`}
                        className={cn(
                            'flex items-center gap-3 transition-colors duration-150',
                            isExpanded ? 'pl-4 pr-3 py-2' : 'justify-center py-2.5',
                            itemActive ? 'text-white' : 'text-white/55 hover:text-white/90'
                        )}
                    >
                        <div className="relative shrink-0 flex items-center justify-center w-5">
                            {item.icon}
                            {item.badge !== undefined && item.badge > 0 && (
                                <span className="absolute -top-2 -right-2.5 flex items-center justify-center min-w-[15px] h-[15px] px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full">
                                    {item.badge > 99 ? '99+' : item.badge}
                                </span>
                            )}
                        </div>
                        {isExpanded && (
                            <span className="text-[13px] flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>
                        )}
                    </Link>
                )}

                {/* Inline children — expanded + group open */}
                {item.isGroup && isExpanded && isOpen && (
                    <div className="pb-1">
                        {item.children!.map(child => {
                            const childActive = child.href === '/leads'
                                ? location.pathname === '/leads' || location.pathname === '/pipeline'
                                : location.pathname === child.href;
                            return (
                                <Link
                                    key={child.href}
                                    to={child.href}
                                    onClick={() => { setIsExpanded(false); setOpenGroups(new Set()); }}
                                    className={cn(
                                        'flex items-center gap-2.5 pl-8 pr-3 py-[7px] text-[12.5px] transition-colors duration-150',
                                        childActive
                                            ? 'text-white'
                                            : 'text-white/45 hover:text-white/85'
                                    )}
                                >
                                    <span className="shrink-0 opacity-70">{child.icon}</span>
                                    {child.label}
                                </Link>
                            );
                        })}
                    </div>
                )}

                {/* Inline children for non-group items with children (expanded) */}
                {!item.isGroup && hasChildren && isExpanded && isOpen && (
                    <div className="pb-1">
                        {item.children!.map(child => (
                            <Link
                                key={child.href}
                                to={child.href}
                                onClick={() => { setIsExpanded(false); setOpenGroups(new Set()); }}
                                className={cn(
                                    'flex items-center gap-2.5 pl-8 pr-3 py-[7px] text-[12.5px] transition-colors duration-150',
                                    location.pathname === child.href
                                        ? 'text-white'
                                        : 'text-white/45 hover:text-white/85'
                                )}
                            >
                                {child.icon && <span className="shrink-0 opacity-70">{child.icon}</span>}
                                {child.label}
                            </Link>
                        ))}
                    </div>
                )}

                {/* Flyout — collapsed mode only */}
                {isFlyoutOpen && !isExpanded && (
                    <div
                        style={{ position: 'fixed', left: '54px', top: flyoutPos.top, bottom: flyoutPos.bottom }}
                        className="z-[200]"
                        onMouseEnter={clearFlyoutTimer}
                        onMouseLeave={handleItemLeave}
                    >
                        {hasChildren ? (
                            <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-1.5 min-w-[192px] overflow-y-auto" style={{ maxHeight: flyoutPos.maxHeight }}>
                                <p className="px-3 pt-1 pb-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                    {item.label}
                                </p>
                                {item.children!.map((child) => (
                                    <Link
                                        key={child.href}
                                        to={child.href}
                                        onClick={() => setFlyoutItem(null)}
                                        className={cn(
                                            'flex items-center px-3 py-2 text-[13px] text-gray-700 hover:bg-brandColor/10 hover:text-brandColor transition-colors',
                                            location.pathname === child.href && 'bg-brandColor/10 text-brandColor'
                                        )}
                                    >
                                        {child.label}
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-gray-900 text-white text-xs rounded-md px-2.5 py-1.5 whitespace-nowrap shadow-lg flex items-center gap-2">
                                {item.label}
                                {item.badge !== undefined && item.badge > 0 && (
                                    <span className="bg-red-500 text-white text-[9px] rounded-full px-1.5 py-0.5 font-bold">
                                        {item.badge}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <aside
            className={cn(
                'fixed left-0 top-0 h-screen bg-brandColor z-50 flex flex-col transition-[width] duration-300 ease-in-out',
                isExpanded ? 'w-[248px] shadow-2xl' : 'w-[52px]'
            )}
        >
            {/* Logo + collapse toggle */}
            <div className={cn(
                'flex items-center h-12 border-b border-white/10 shrink-0',
                isExpanded ? 'px-3 justify-between' : 'justify-center'
            )}>
                {isExpanded ? (
                    <>
                        <Link to="/dashboard" className="flex flex-col items-start select-none">
                            <span className="text-[15px] font-bold text-white tracking-wide leading-none">ZEROHARM</span>
                            <span className="text-[9px] font-semibold text-white/70 tracking-widest uppercase leading-none mt-0.5">Suite</span>
                        </Link>
                        <button
                            onClick={() => setIsExpanded(false)}
                            className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded transition-colors"
                            title="Collapse sidebar"
                        >
                            <ChevronLeft size={15} />
                        </button>
                    </>
                ) : (
                    <button
                        onClick={() => setIsExpanded(true)}
                        className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded transition-colors"
                        title="Expand sidebar"
                    >
                        <ChevronRight size={15} />
                    </button>
                )}
            </div>

            {/* Global Search */}
            {isExpanded ? (
                <div className="px-3 pt-2.5 pb-1 shrink-0">
                    <GlobalSearch variant="bar" className="!w-full" />
                </div>
            ) : (
                <div className="flex justify-center py-1.5 shrink-0">
                    <GlobalSearch variant="icon" />
                </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 space-y-0.5">
                {mainItems.map(renderItem)}

                <div className="mx-3 my-2 border-t border-white/10" />

                {mgmtItems.map(renderItem)}

                {isSuperAdmin && (
                    <>
                        <div className="mx-3 my-2 border-t border-white/10" />
                        {adminItems.map(renderItem)}
                    </>
                )}
            </nav>

            {/* Bottom utilities */}
            <div className="shrink-0 border-t border-white/10">

                {/* Notification bell */}
                <div className="relative" ref={notifRef}>
                    <button
                        onClick={openNotifPanel}
                        className={cn(
                            'w-full flex items-center gap-3 transition-colors duration-150',
                            isExpanded ? 'pl-4 pr-3 py-2.5' : 'justify-center py-2.5',
                            showNotifPanel ? 'text-white' : 'text-white/50 hover:text-white/90'
                        )}
                        title="Notifications"
                    >
                        <div className="relative shrink-0 flex items-center justify-center w-5">
                            <Bell size={16} />
                            {unreadCount > 0 && (
                                <span className="absolute -top-2 -right-2.5 flex items-center justify-center min-w-[15px] h-[15px] px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full">
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                        </div>
                        {isExpanded && (
                            <span className="text-[13px] flex-1 text-left">Notifications</span>
                        )}
                    </button>

                    {/* Notification panel — opens to the right */}
                    {showNotifPanel && (
                        <div className="absolute left-full bottom-0 ml-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-[60] overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                                <span className="font-semibold text-gray-800 text-sm">Notifications</span>
                                <div className="flex items-center gap-2">
                                    {unreadCount > 0 && (
                                        <button onClick={markAllRead} className="text-xs text-brandColor hover:underline">
                                            Mark all read
                                        </button>
                                    )}
                                    <button onClick={() => setShowNotifPanel(false)}>
                                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                    </button>
                                </div>
                            </div>

                            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                                {notifications.length === 0 ? (
                                    <div className="py-8 text-center text-sm text-gray-400">No notifications</div>
                                ) : (
                                    notifications.map(n => (
                                        <button
                                            key={n.uuid}
                                            onClick={() => markRead(n)}
                                            className={cn(
                                                'w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3',
                                                !n.is_read && 'bg-blue-50/50'
                                            )}
                                        >
                                            {notifIcon(n.type)}
                                            <div className="flex-1 min-w-0">
                                                <p className={cn('text-sm text-gray-800 leading-snug', !n.is_read && 'font-semibold')}>{n.title}</p>
                                                {n.body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>}
                                                <p className="text-[11px] text-gray-400 mt-1">{safeDate(n.created_at)}</p>
                                            </div>
                                            {!n.is_read && <span className="h-2 w-2 rounded-full bg-brandColor shrink-0 mt-1" />}
                                        </button>
                                    ))
                                )}
                            </div>

                            <div className="border-t px-4 py-2 bg-gray-50">
                                <Link
                                    to="/claims"
                                    onClick={() => setShowNotifPanel(false)}
                                    className="text-xs text-brandColor hover:underline"
                                >
                                    View all claims →
                                </Link>
                            </div>
                        </div>
                    )}
                </div>

                {/* User profile */}
                <div className={cn(
                    'flex items-center border-t border-white/10',
                    isExpanded ? 'px-3 py-2' : 'justify-center py-2'
                )}>
                    <UserProfileDropdown placement="right" />
                    {isExpanded && (
                        <span className="ml-3 text-[13px] text-white/50 truncate">
                            {userData?.data?.userName || 'Profile'}
                        </span>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default AppSidebar;
